import React, { useEffect, useState, useCallback } from 'react';
import { api, Stats, SchedulerStatus, SchedulerRun } from '../api/client';
import StatsCard from '../components/StatsCard';
import SchedulerStatusComp from '../components/SchedulerStatus';

// ─── Icons ────────────────────────────────────────────────────────────────────
const IconMail   = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>;
const IconCheck  = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><path d="m9 12 2 2 4-4"/></svg>;
const IconX      = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><path d="m15 9-6 6M9 9l6 6"/></svg>;
const IconClock  = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>;
const IconStar   = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>;
const IconRefresh = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8"/><path d="M21 3v5h-5"/><path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"/><path d="M8 16H3v5"/></svg>;

// ─── Status chip (Fluent palette) ─────────────────────────────────────────────
const RunStatus: React.FC<{ status: string }> = ({ status }) => {
  const cfg: Record<string, { bg: string; text: string }> = {
    COMPLETED: { bg: '#DFF6DD', text: '#107C10' },
    FAILED:    { bg: '#FDE7E9', text: '#A4262C' },
    RUNNING:   { bg: '#EBF3FB', text: '#0078D4' },
  };
  const c = cfg[status] ?? { bg: '#F3F2F1', text: '#605E5C' };
  return (
    <span style={{ background: c.bg, color: c.text, borderRadius: '2px', padding: '2px 8px', fontSize: '11px', fontWeight: 600, letterSpacing: '.02em' }}>
      {status}
    </span>
  );
};

// ─── Component ────────────────────────────────────────────────────────────────
const Dashboard: React.FC = () => {
  const [stats, setStats] = useState<Stats | null>(null);
  const [schedulerStatus, setSchedulerStatus] = useState<SchedulerStatus | null>(null);
  const [runs, setRuns] = useState<SchedulerRun[]>([]);
  const [loading, setLoading] = useState(true);
  const [triggering, setTriggering] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  const loadData = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);
    try {
      const [statsData, schedulerData] = await Promise.all([
        api.getStats(),
        api.getSchedulerStatus(),
      ]);
      setStats(statsData);
      setSchedulerStatus(schedulerData);
      setRuns(statsData.recentRuns);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    loadData();
    const interval = setInterval(() => loadData(true), 30000);
    return () => clearInterval(interval);
  }, [loadData]);

  const handleTrigger = async () => {
    setTriggering(true);
    try {
      await api.triggerRun();
      setTimeout(() => loadData(true), 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to trigger run');
    } finally {
      setTriggering(false);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '32px' }}>
        <div style={{ marginBottom: '24px' }}>
          <div className="skeleton" style={{ height: 28, width: 200, marginBottom: 8 }} />
          <div className="skeleton" style={{ height: 14, width: 280 }} />
        </div>
        <div style={{ display: 'flex', gap: '16px', marginBottom: '24px' }}>
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="card skeleton" style={{ flex: 1, height: 110 }} />
          ))}
        </div>
        <div className="card skeleton" style={{ height: 240 }} />
      </div>
    );
  }

  return (
    <div style={{ padding: '32px' }} className="anim-fadeUp">
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '28px' }}>
        <div>
          <h1 style={{ fontSize: '22px', fontWeight: 800, color: 'var(--text)', letterSpacing: '-0.02em' }}>Dashboard</h1>
          <p style={{ fontSize: '13px', color: 'var(--text-3)', marginTop: '3px' }}>
            Email extraction activity overview
            {refreshing && <span style={{ marginLeft: 8, color: 'var(--accent)' }}>Refreshing…</span>}
          </p>
        </div>
        <button className="btn-ghost" onClick={() => loadData(true)} disabled={refreshing}>
          <span className={refreshing ? 'anim-spin' : ''} style={{ display: 'inline-block' }}><IconRefresh /></span>
          Refresh
        </button>
      </div>

      {error && (
        <div style={{ background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 'var(--radius)', padding: '12px 16px', marginBottom: '20px', color: '#dc2626', fontSize: '13px' }}>
          {error}
        </div>
      )}

      {/* Scheduler status */}
      {schedulerStatus && (
        <SchedulerStatusComp status={schedulerStatus} onTrigger={handleTrigger} triggering={triggering} />
      )}

      {/* Stats cards */}
      {stats && (
        <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginBottom: '24px' }}>
          <StatsCard label="Total Extractions" value={stats.total}       icon={<IconMail />}  color="#2563eb" lightColor="#dbeafe" />
          <StatsCard label="Completed"          value={stats.completed}  icon={<IconCheck />} color="#10b981" lightColor="#d1fae5" />
          <StatsCard label="Failed"             value={stats.failed}     icon={<IconX />}     color="#ef4444" lightColor="#fee2e2" />
          <StatsCard label="Processing"         value={stats.processing} icon={<IconClock />} color="#f59e0b" lightColor="#fef3c7" />
          <StatsCard label="Today"              value={stats.todayCount} icon={<IconStar />}  color="#8b5cf6" lightColor="#ede9fe" />
        </div>
      )}

      {/* Runs table */}
      <div className="card" style={{ overflow: 'hidden' }}>
        <div style={{ padding: '18px 20px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h2 style={{ fontSize: '15px', fontWeight: 700, color: 'var(--text)' }}>Recent Scheduler Runs</h2>
            <p style={{ fontSize: '12px', color: 'var(--text-3)', marginTop: '2px' }}>Last {runs.length} runs</p>
          </div>
        </div>

        {runs.length === 0 ? (
          <div style={{ padding: '48px', textAlign: 'center' }}>
            <div style={{ fontSize: '28px', marginBottom: '8px' }}>🕐</div>
            <p style={{ color: 'var(--text-3)', fontSize: '13px' }}>No scheduler runs yet</p>
          </div>
        ) : (
          <table className="ms-table">
            <thead>
              <tr>
                {['Started At', 'Status', 'Found', 'Processed', 'Skipped', 'Duration', 'Error'].map((h, i) => (
                  <th key={h} style={{ textAlign: i >= 2 && i <= 5 ? 'right' : 'left' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {runs.map((run) => (
                <tr key={run.id}>
                  <td>{new Date(run.startedAt).toLocaleString()}</td>
                  <td><RunStatus status={run.status} /></td>
                  <td style={{ textAlign: 'right', fontWeight: 600, color: 'var(--text)' }}>{run.emailsFound}</td>
                  <td style={{ textAlign: 'right', fontWeight: 600, color: 'var(--success)' }}>{run.emailsProcessed}</td>
                  <td style={{ textAlign: 'right', color: 'var(--text-3)' }}>{run.emailsSkipped}</td>
                  <td style={{ textAlign: 'right', color: 'var(--text-3)' }}>
                    {run.durationMs ? `${(run.durationMs / 1000).toFixed(1)}s` : '—'}
                  </td>
                  <td style={{ color: 'var(--danger)', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {run.error ?? '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};


export default Dashboard;
